import { ComponentChildren, JSX, render } from 'preact';
import { Icon } from './icons.js';
import { Input } from './input.js';
import { Maybe } from '../../common/data.js';
import { isSome, logRejection } from '../../common/util.js';
import { useEffect } from 'preact/compat';
import { useRef, useState } from 'preact/hooks';
import { getSiteContext } from '../templates/site.js';
import { SiteContext } from '../context.js';
import { ApiError } from '../../common/errors.js';
import { ErrorMessage } from './error.js';

export function Popup({ close, children, focusSelector, role }: {
	close: () => void;
	children?: ComponentChildren;
	focusSelector?: string;
	role?: JSX.AriaRole;
}) {

	let screen: EventTarget;
	
	const popup = useRef<HTMLDivElement>(null);
	useEffect(() => popup.current?.querySelector<HTMLElement>(focusSelector ?? 'button')?.focus(), []);
	
	return <div>
		<div className="popup-screen" ref={it => screen = it!} onMouseDown={e => screen == e.target && close()}>
			<div className="popup" ref={popup} role={role ?? 'dialog'}>
				<button className="popup__close" onClick={close}>
					<Icon name="close" />
				</button>
				{children}
			</div>
		</div>
	</div>;
	
}


export const showAlert = async (children: ComponentChildren | ((resolve: () => void) => ComponentChildren), type?: Maybe<'modal' | 'info' | 'warning' | 'error'>, title?: string) => {
	
	const div = document.createElement('div', {});
	document.body.appendChild(div);
	
	await new Promise<void>(resolve => {
		let pop = <Popup close={resolve} focusSelector=".alert__button">
			<div class="alert">
				{title && <h2 class="alert__title">{title}</h2>}
				<div class="alert__body">
					{isSome(type) && type !== 'modal' && <div class={`alert__icon alert__icon--${type}`} title={type.toUpperCase()}><Icon name="info" /></div>}
					{typeof children == 'function' ? children(resolve) : children}
				</div>
				{type !== 'modal' && <button class="alert__button" onClick={() => resolve()}>Ok</button>}
			</div>
		</Popup>;
		const sc = getSiteContext();
		if (sc) pop = <SiteContext.Provider value={sc} children={pop} />;
		render(pop, div);
	});
	
	div.remove();
};

export const showSuccessAlert = (children: ComponentChildren | ((resolve: () => void) => ComponentChildren)) => {
	return showAlert(children, 'info');
};
export const showErrorAlert = async (children: ComponentChildren | ((resolve: () => void) => ComponentChildren) | Error | string) => {
	if (children instanceof Error) logRejection(children);
	if (children instanceof Error || typeof children == 'string') children = <ErrorMessage error={children} />;
	return await showAlert(children);
};
export const showErrorAndThrow = async (error: ApiError): Promise<never> => {
	await showErrorAlert(error);
	throw error;
};

interface ConfirmOptions {
	width: number;
}

export const showConfirm = async (children: ComponentChildren | ((resolve: (value: boolean) => void) => ComponentChildren), buttons: string[] = ['OK', 'Cancel'], options?: ConfirmOptions) => {
	
	const div = document.createElement('div', {});
	document.body.appendChild(div);
	(document.activeElement as any)?.blur?.();
	
	const res = await new Promise<boolean>(resolve => {
		const pop = <Popup close={() => resolve(false)} focusSelector=".alert__cancel">
			<div class="alert" style={{ width: options?.width }}>
				<div class="alert__body">
					<div class="alert__icon"><Icon name="info" /></div>
					{typeof children == 'function' ? children(resolve) : children}
				</div>
				<div class="alert__actions">
					<button class="alert__confirm" onClick={() => resolve(true)}>{buttons[0]}</button>
					<button class="alert__cancel" onClick={() => resolve(false)}>{buttons[1]}</button>
				</div>
			</div>
		</Popup>;
		render(pop, div);
	});
	
	div.remove();
	return res;
};

interface PromptOptions {
	initialText?: string;
}

function PromptBody({ initialText, children, done, buttons }: {
	initialText: Maybe<string>;
	children: ComponentChildren;
	done: (res: string | false) => void;
	buttons: string[];
}) {
	const [text, setText] = useState(initialText ?? '');
	return <div class="alert">
		<div class="alert__body">
			<div class="alert__icon"><Icon name="info" /></div>
			{children}
			<br />
			<Input value={text} handler={setText} attributes={{
				onKeyUp: (e: KeyboardEvent) => {
					if (e.key === 'Enter') done(text);
				},
			}} />
		</div>
		<div class="alert__actions">
			<button onClick={() => done(text)}>{buttons[0]}</button>
			<button onClick={() => done(false)}>{buttons[1]}</button>
		</div>
	</div>;
}

export const showPrompt = async (children: ComponentChildren, buttons: string[] = ['OK', 'Cancel'], options?: PromptOptions) => {
	
	const div = document.createElement('div', {});
	document.body.appendChild(div);
	(document.activeElement as any)?.blur?.();
	
	const res = await new Promise<false|string>(resolve => {
		const pop = <Popup close={() => resolve(false)} focusSelector="input">
			<PromptBody done={resolve} buttons={buttons} initialText={options?.initialText}>
				{children}
			</PromptBody>
		</Popup>;
		render(pop, div);
	});
	
	div.remove();
	return res;
	
};

export function alertOnError<T, A extends any[]>(fn: (this: T, ...args: A) => Promise<void>, _context?: ClassMethodDecoratorContext<T>) {
	return function (this: T, ...args: A) {
		return fn.call(this, ...args).catch(showErrorAlert);
	};
}
